import { defineComponent as _defineComponent } from 'vue'
import { toDataModel, updateModel, setState, getChildModel, getState } from 'ah-common-lib/src/form/helpers';
import { FormDefinition, FormEvent } from 'ah-common-lib/src/form/interfaces';
import { FormValidation } from 'ah-common-lib/src/form/interfaces';
import { useSettingsStore } from '@/app/store/settingsModule';
import { countryHasStates, countryNameFromCC } from 'ah-common-lib/src/helpers/countries';
import { addressFM } from '@/app/helpers/registration/forms';
import { computed, onBeforeMount, reactive, watch } from 'vue';
import { Address } from 'ah-api-gateways';
import { isEqual, sortBy } from 'lodash';


export default /*#__PURE__*/_defineComponent({
  __name: 'RegistrationAddressForm',
  props: {
    address: null,
    remove: null
  },
  emits: ["update:address", "update:validation"],
  setup(__props: any, { expose, emit }: { emit: ({
  (e: 'update:address', value: Partial<Address>): void;
  (e: 'update:validation', value: FormValidation<Partial<Address>>): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { address?: Partial<Address>; remove?: string[] };





const addressForm = reactive<FormDefinition>({
  form: addressFM(),
  validation: null,
});

const settingsStore = useSettingsStore();

const validation = computed(() => {
  return {
    ...addressForm.validation,
    $model: addressForm.form,
    $invalid: !!addressForm.validation?.$invalid,
    $dirty: !!addressForm.validation?.$dirty,
  } as FormValidation<any>;
});

function getModel(fieldName: string) {
  return getChildModel(addressForm.form, fieldName);
}

onBeforeMount(() => {
  const nationalityField = getChildModel(addressForm.form, 'nationality')!;
  setState(
    nationalityField,
    'options',
    sortBy(
      settingsStore.allCountries.map((c) => ({
        value: c.cc,
        label: c.nationality,
      })),
      (i) => i.label
    )
  );
});

watch(
  () => props.address,
  () => updateModel(addressForm.form, props.address ?? {}),

  { immediate: true }
);

watch(
  () => props.address?.countryCode,
  () => {
    const stateOrProvinceCode = getChildModel(addressForm.form, 'stateOrProvince')!;
    const currentState = getState(stateOrProvinceCode, 'fieldType');
    const countryCode = props.address?.countryCode || '';

    const countryName = countryNameFromCC(countryCode);
    if (countryName && addressForm.form.countryName !== countryName) {
      addressForm.form.countryName = countryName;
    }

    if (countryHasStates(countryCode)) {
      addressForm.form.stateOrProvince = null;
      setState(stateOrProvinceCode, 'fieldType', 'select');
      settingsStore.loadISOCodes({ countryCode }).then((states) => {
        setState(
          stateOrProvinceCode,
          'options',
          states.map((s) => ({
            value: s.code,
            label: s.subdivisionName,
          }))
        );
      });
    } else {
      if (currentState !== 'text') {
        // only clears input if previously it was a country with states
        addressForm.form.stateOrProvince = null;
      }
      setState(stateOrProvinceCode, 'fieldType', 'text');
    }
  },
  { immediate: true }
);

function onFormEvent(event: FormEvent) {
  if (event.event === 'form-field-set-value') {
    emit('update:address', { ...props.address, ...toDataModel(addressForm.form) });
  }
}

watch(
  validation,
  (newVal, oldVal) => {
    if (!isEqual(newVal, oldVal)) {
      emit('update:validation', validation.value);
    }
  },
  { immediate: true }
);

watch(
  () => props.remove,
  () => {
    props.remove?.forEach((fieldName) => {
      const field = getChildModel(addressForm.form, fieldName);
      if (field) {
        setState(field, 'required', false);
        setState(field, 'hidden', true);
        addressForm.form[fieldName] = undefined;
      }
    });
  },
  { immediate: true }
);

expose({ getModel });

const countryCodeField = getChildModel(addressForm.form, 'countryCode')!;
settingsStore.loadCountries(true).then(() => {
  const countries = settingsStore.allCountries.map((country) => ({
    value: country.cc,
    label: country.name,
  }));

  setState(countryCodeField, 'options', countries);
});

return { __sfc: true,props, emit, addressForm, settingsStore, validation, getModel, onFormEvent, countryCodeField }
}

})